import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Undefinable,
  PaymentMode,
  TaxInterface,
  DeliveryType,
  ShipmentService,
  AccountInterface,
  PaymentInterface,
  ShipmentInterface,
  DocumentInterface,
  ShipmentStatus,
  LocationInterface,
  ItemInterface,
} from "../../interfaces";
import { CouponDTO } from "../../interfaces/Dtos/CouponDTO";
import { AddressValidationResponseItem } from "../../interfaces/DHLInterface";

export enum ShipmentCreatePhase {
  SHIPMENTS,
  DOCUMENT,
  PAYMENT,
  RESUME,
}

export interface ShipmentDraft extends Partial<ShipmentInterface> {
  cityValue?: string;
  cityName?: string;
  internationalAddress?: LocationInterface;
  countryCode?: string;
  dhlDestination?: AddressValidationResponseItem;
  dhlItem?: ItemInterface;
  /**
   * Deterministic fingerprint of every rate-affecting input that produced the
   * current `items` quote (excludes saleDate / time). Used to skip recomputing
   * the rate when returning to the form (e.g. Regresar) without any change.
   * `undefined` means "no cached quote".
   */
  quoteSignature?: string;
  /**
   * Whether the promotion held by this already-saved guide has been released
   * since its last save. Set to `false` when the guide is saved (Continuar),
   * and to `true` after the first rate-relevant edit triggers the release PUT,
   * so the release fires at most once per save cycle.
   */
  promotionReleased?: boolean;
  /**
   * Stable client-side identity, assigned by the reducers below and never sent
   * to the backend. `ShipmentFormList` keys its `<ShipmentForm>` instances by
   * it: keying by array index instead makes React reuse a mounted form against
   * a different draft when a guide in the middle of a lote is deleted, so the
   * form's local state (declared value, DHL lookups, …) leaks onto the guide
   * that took the removed one's position.
   */
  draftKey?: number;
}

/**
 * Gives a `draftKey` to every draft that lacks one, numbering from above the
 * highest key already in the array so no two drafts alive at the same time
 * share a key. Drafts that already carry one keep both the key and their
 * object identity — that is what lets the form list survive a splice.
 */
const assignDraftKeys = (drafts: ShipmentDraft[]): ShipmentDraft[] => {
  let nextKey = drafts.reduce(
    (max, draft) => Math.max(max, (draft.draftKey ?? -1) + 1),
    0
  );

  return drafts.map((draft) =>
    draft.draftKey === undefined ? { ...draft, draftKey: nextKey++ } : draft
  );
};

interface ShipmentCreateState {
  coupon?: CouponDTO;
  taxes: TaxInterface[];
  owner?: AccountInterface;
  phase: ShipmentCreatePhase;
  document?: DocumentInterface;
  payments: PaymentInterface[];
  shipments: ShipmentInterface[];
  shipmentDrafts: ShipmentDraft[];
}

interface ShipmentCreateInterfaceAction {
  index: number;
  shipment: ShipmentDraft;
}

const initialState: ShipmentCreateState = {
  shipments: [],
  shipmentDrafts: [
    {
      pieces: [],
      items: [],
      deliveryType: DeliveryType.AT_OFFICE,
      paymentMode: PaymentMode.COD,
      service: ShipmentService.STANDARD,
      status: ShipmentStatus.DRAFT,
      draftKey: 0,
    },
  ],
  phase: ShipmentCreatePhase.SHIPMENTS,
  payments: [],
  taxes: [],
};

export const shipmentCreateSlice = createSlice({
  name: "pickup",
  initialState,
  reducers: {
    setShipmentCreateList: (
      state,
      action: PayloadAction<ShipmentInterface[]>
    ) => {
      state.shipments = action.payload;
    },
    setShipmentCreateDrafts: (
      state,
      action: PayloadAction<ShipmentDraft[]>
    ) => {
      state.shipmentDrafts = assignDraftKeys(action.payload);
    },
    setShipmentCreateDraft(
      state,
      action: PayloadAction<ShipmentCreateInterfaceAction>
    ) {
      const { index, shipment } = action.payload;
      const currentDraft = state.shipmentDrafts[index];
      state.shipmentDrafts[index] = { ...currentDraft, ...shipment };
    },
    setShipmentCreatePhase: (
      state,
      action: PayloadAction<ShipmentCreatePhase>
    ) => {
      state.phase = action.payload;
    },
    setShipmentCreateOwner: (
      state,
      action: PayloadAction<Undefinable<AccountInterface>>
    ) => {
      state.owner = action.payload;
    },
    setShipmentCreatePayments: (
      state,
      action: PayloadAction<PaymentInterface[]>
    ) => {
      state.payments = action.payload;
    },
    setShipmentCreateTaxes: (state, action: PayloadAction<TaxInterface[]>) => {
      state.taxes = action.payload;
    },
    setShipmentCreateDocument: (
      state,
      action: PayloadAction<DocumentInterface>
    ) => {
      state.document = action.payload;

      // Set the other data
      state.owner = action.payload.accountOwner;
      state.taxes = action.payload.taxes;
      state.payments = action.payload.payments;
      // The shipment list is NOT derived from the document any more. A document
      // read from the server carries light rows with no `items`, and the
      // payment screen computes the subtotal and the VAT by iterating them, so
      // deriving it here let a light row in through a bare cast and threw on
      // render. Whoever holds full guías feeds them with setShipmentCreateList.
    },
    setShipmentCreateCoupon: (
      state,
      action: PayloadAction<CouponDTO | undefined>
    ) => {
      state.coupon = action.payload;
    },
    restartShipmentCreate: (state, action: PayloadAction<ShipmentDraft[]>) => {
      state.shipmentDrafts = assignDraftKeys(action.payload);
      state.phase = ShipmentCreatePhase.SHIPMENTS;
      state.payments = [];
      state.taxes = [];
      state.document = undefined;
      state.owner = undefined;
      state.coupon = undefined;
      state.shipments = [];
    },
    clearShipmentCreate: (state) => {
      state.payments = [];
      state.shipments = [];
      state.taxes = [];
      state.document = undefined;
      state.owner = undefined;
      state.coupon = undefined;
      state.phase = ShipmentCreatePhase.SHIPMENTS;
      state.shipmentDrafts = [
        {
          pieces: [],
          items: [],
          service: ShipmentService.STANDARD,
          deliveryType: DeliveryType.AT_OFFICE,
          status: ShipmentStatus.DRAFT,
          declaredValue: "",
          destination: "",
          observations: "",
          draftKey: 0,
        },
      ];
    },
  },
});

export const {
  clearShipmentCreate,
  setShipmentCreateList,
  setShipmentCreateDraft,
  setShipmentCreateTaxes,
  setShipmentCreateCoupon,
  setShipmentCreateDocument,
  setShipmentCreatePhase,
  setShipmentCreateOwner,
  setShipmentCreateDrafts,
  setShipmentCreatePayments,
  restartShipmentCreate,
} = shipmentCreateSlice.actions;

export default shipmentCreateSlice.reducer;
